// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { IntegrationStatus } from '@/components/integration-status';
import { LastUpdatedDate } from '@/components/last-updated-date';
import { Link } from '@/components/link';
import { VisibilityOffOutlined } from '@/constants/hardfin-standard-icons';
import { Item } from '@/items/api/types';
import { ItemTypeChip } from '@/items/components/item-type-chip';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

interface Props {
  item: Item;
  hasIntegration: boolean;
}

/**
 * A single row in the items table.
 * Displays item type, SKU, name, description, and last updated date.
 * Archived items are shown with disabled text color and an eye icon.
 */
export function ItemsTableRow(props: Props) {
  const { item, hasIntegration } = props;

  // Use disabled text color for archived items to visually indicate their status
  const textColor = item.isArchived ? 'text.disabled' : 'text.primary';

  return (
    <TableRow hover>
      {/* Integration status */}
      {hasIntegration && (
        <TableCell sx={{ width: '3rem', pl: 2, pr: 0, whiteSpace: 'nowrap' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IntegrationStatus status={item.integrations} />
          </Box>
        </TableCell>
      )}
      {/* Type - chip with icon */}
      <TableCell>
        <ItemTypeChip item={item} />
      </TableCell>
      {/* Name - link to item detail page */}
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Link
            to="/catalog/items/$itemKey"
            params={{ itemKey: item.key }}
            variant="subtitle2"
            color={item.isArchived ? 'text.disabled' : 'primary'}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.name}
          </Link>
          {item.isArchived && (
            <Tooltip title="This item is archived" arrow>
              <VisibilityOffOutlined fontSize="small" color="disabled" />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      {/* SKU */}
      <TableCell>
        <Typography variant="body2" color={textColor} noWrap>
          {item.sku ?? '-'}
        </Typography>
      </TableCell>
      {/* Description */}
      <TableCell>
        <Typography
          variant="body2"
          color={item.isArchived ? 'text.disabled' : 'text.secondary'}
          noWrap
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.description ?? '-'}
        </Typography>
      </TableCell>
      {/* Asset count - show count for DEVICE (linked to assets), em dash for others */}
      <TableCell align="right">
        {item.assetCount !== null ? (
          <Link
            to="/assets"
            variant="body2"
            color={textColor}
            search={{ products: [item.id] }}
          >
            {formatNumber(item.assetCount)}
          </Link>
        ) : (
          <Typography variant="body2" color={textColor}>
            &mdash;
          </Typography>
        )}
      </TableCell>
      {/* Spacer */}
      <TableCell />
      {/* Last updated - date with the full timestamp in the tooltip */}
      <TableCell>
        <LastUpdatedDate
          date={item.lastUpdatedAt}
          by={item.lastUpdatedBy}
          color={item.isArchived ? 'text.disabled' : 'text.secondary'}
        />
      </TableCell>
    </TableRow>
  );
}
